import axios from 'axios';
import type { 
  FuelType, 
  Retailer, 
  FuelStation, 
  CurrentPrice, 
  ApiResponse, 
  HealthCheck,
  ClusterData
} from '@/types';

// Get API base URL from environment variable, fallback to relative path for development
const apiBaseURL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

const api = axios.create({
  baseURL: apiBaseURL,
  timeout: 10000,
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

export const healthApi = {
  check: (): Promise<HealthCheck> => 
    api.get('/health').then(res => res.data),
};

export const fuelTypesApi = {
  getAll: (): Promise<FuelType[]> => 
    api.get('/fuel-types').then(res => res.data.data || res.data),
};

export const retailersApi = {
  getAll: (activeOnly = true): Promise<Retailer[]> => 
    api.get('/retailers', { params: { active_only: activeOnly } }).then(res => res.data.data || res.data),
};

export const stationsApi = {
  getAll: (params?: {
    retailer_id?: number;
    postcode?: string;
    active_only?: boolean;
    limit?: number;
  }): Promise<FuelStation[]> => 
    api.get('/stations', { params }).then(res => res.data.data || res.data),

  getNearby: (params: {
    latitude: number;
    longitude: number;
    radius?: number;
    active_only?: boolean;
    limit?: number;
  }): Promise<FuelStation[]> => 
    api.get('/stations/nearby', { params }).then(res => res.data.data),

  getClustered: (params: {
    zoom_level: number;
    south?: number;
    north?: number;
    west?: number;
    east?: number;
    active_only?: boolean;
  }): Promise<ClusterData[]> => 
    api.get('/stations/clustered', { params }).then(res => res.data.data),
};

export const pricesApi = {
  getCurrent: (params?: {
    fuel_code?: string;
    postcode?: string;
    retailer?: string;
    limit?: number;
    sort_by_price?: boolean;
  }): Promise<CurrentPrice[]> => 
    api.get('/prices/current', { params }).then(res => res.data.data || res.data),

  getByStations: (stationIds: number[], params?: {
    fuel_code?: string;
    sort_by_price?: boolean;
  }): Promise<CurrentPrice[]> => 
    api.post('/prices/by-stations', stationIds, { params }).then(res => res.data.data),

  getCheapest: (params: {
    fuel_code: string;
    postcode?: string;
    limit?: number;
  }): Promise<CurrentPrice[]> => 
    api.get('/prices/cheapest', { params }).then(res => res.data.data || res.data),

  getByPostcode: (postcodeArea: string, fuelCode?: string): Promise<ApiResponse<any>> => 
    api.get(`/prices/by-postcode/${postcodeArea}`, { 
      params: fuelCode ? { fuel_code: fuelCode } : {} 
    }).then(res => res.data),
};

export const syncApi = {
  trigger: (): Promise<ApiResponse<any>> => 
    api.post('/sync/trigger').then(res => res.data),
};

export default api;